'use client'

import React from 'react'

import { twMerge } from 'tailwind-merge'

import { BadgeV2 } from '../../common/BadgeV2'
import { ContentPanelV2 } from '../../common/ContentPanelV2'
import { DataBlockV2 } from '../../common/DataBlockV2'
import { DataSheetSkeleton } from '../../common/DataSheet'
import { PageTitleV2 } from '../../common/PageTitleV2'
import { SidebarWidgetV2 } from '../../common/SidebarWidgetV2'
import { TabPanelV2 } from '../../common/TabPanelV2'
import { TimelineV2 } from '../../common/TimelineV2'

// ============================================================================
// Base Skeleton Primitive
// ============================================================================

interface SkeletonProps {
  className?: string
}

const Skeleton: React.FC<SkeletonProps> = ({ className }) => (
  <div
    className={twMerge(
      'animate-pulse rounded bg-primary-200 dark:bg-primary-700',
      className,
    )}
  />
)

// ============================================================================
// Funding Gauge Skeleton
// ============================================================================

const FundingGaugeSkeleton: React.FC = () => (
  <div className="rounded-xl border border-primary-200 bg-white p-6 shadow-sm dark:border-primary-700 dark:bg-primary-900">
    {/* Header */}
    <div className="mb-4 flex items-center gap-2">
      <Skeleton className="h-4 w-4" />
      <Skeleton className="h-5 w-32" />
    </div>
    {/* Progress bar */}
    <div className="mb-4 h-3 w-full rounded-full bg-primary-100 dark:bg-primary-800">
      <Skeleton className="h-3 w-3/4 rounded-full" />
    </div>
    {/* Stats grid */}
    <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
      <div className="space-y-1">
        <Skeleton className="h-3 w-20" />
        <Skeleton className="h-6 w-28" />
      </div>
      <div className="space-y-1">
        <Skeleton className="h-3 w-24" />
        <Skeleton className="h-6 w-28" />
      </div>
      <div className="space-y-1">
        <Skeleton className="h-3 w-20" />
        <Skeleton className="h-6 w-28" />
      </div>
      <div className="space-y-1">
        <Skeleton className="h-3 w-20" />
        <Skeleton className="h-6 w-28" />
      </div>
    </div>
  </div>
)

// ============================================================================
// Main Skeleton Component
// ============================================================================

/**
 * AwardDisplayV2Skeleton - Loading skeleton for award detail page
 *
 * Composes V2 component skeletons to match the actual layout exactly,
 * minimizing layout shift when content loads.
 */
const AwardDisplayV2Skeleton: React.FC = () => {
  return (
    <>
      {/* Header Block */}
      <div className="mb-6">
        <div className="flex flex-col gap-3">
          {/* Badges Row */}
          <div className="flex items-center gap-2">
            <BadgeV2.Skeleton width="sm" />
            <BadgeV2.Skeleton width="md" />
            <BadgeV2.Skeleton width="sm" />
          </div>

          {/* Title + Actions Row */}
          <div className="flex flex-col justify-between gap-4 lg:flex-row lg:items-end">
            <div className="max-w-4xl flex-1 space-y-1.5">
              {/* Title skeleton */}
              <PageTitleV2.TitleSkeleton width="w-[40rem]" />
              {/* Agency subtitle */}
              <PageTitleV2.SubtitleSkeleton width="w-64" showIcon />
              {/* Award IDs */}
              <div className="flex items-center gap-3 pt-0.5">
                <Skeleton className="h-5 w-32 rounded" />
                <Skeleton className="h-5 w-40 rounded" />
              </div>
            </div>

            {/* Action Buttons skeleton */}
            <div className="flex shrink-0 items-center gap-2">
              <Skeleton className="h-9 w-9 rounded-lg" />
              <Skeleton className="h-9 w-28 rounded-lg" />
            </div>
          </div>
        </div>
      </div>

      {/* Data Grid - 4 columns: Obligated, Current, Potential, Contract Type */}
      <div className="mb-6 grid grid-cols-2 gap-3 md:grid-cols-4">
        <DataBlockV2.Skeleton showSubValue />
        <DataBlockV2.Skeleton showSubValue />
        <DataBlockV2.Skeleton showSubValue />
        <DataBlockV2.Skeleton />
      </div>

      {/* Main Layout - 12 column grid */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
        {/* Main Content Column */}
        <div className="flex flex-col gap-4 lg:col-span-8">
          {/* Tabs skeleton */}
          <TabPanelV2.ListSkeleton
            tabCount={4}
            tabWidths={['md', 'lg', 'lg', 'md']}
          />

          {/* Content Panel skeleton */}
          <div className="mt-2 overflow-hidden rounded-lg border border-primary-200 bg-white shadow-sm dark:border-primary-700 dark:bg-primary-900">
            <ContentPanelV2.Skeleton
              lineCount={6}
              showHeader
              showFooter={false}
              className="border-0 shadow-none"
            />
          </div>

          {/* Funding Gauge Section skeleton */}
          <FundingGaugeSkeleton />

          {/* Federal Accounts Section skeleton */}
          <div className="rounded-xl border border-primary-200 bg-white shadow-sm dark:border-primary-700 dark:bg-primary-900">
            {/* Header */}
            <div className="flex items-center gap-2 border-b border-primary-100 px-4 py-3 dark:border-primary-800">
              <Skeleton className="h-4 w-4" />
              <Skeleton className="h-4 w-40" />
            </div>
            {/* Table header */}
            <div className="grid grid-cols-4 gap-4 border-b border-primary-100 bg-primary-50 px-4 py-2 dark:border-primary-800 dark:bg-primary-800">
              <Skeleton className="h-3 w-20" />
              <Skeleton className="h-3 w-16" />
              <Skeleton className="h-3 w-24" />
              <Skeleton className="h-3 w-12" />
            </div>
            {/* Table rows */}
            <div className="divide-y divide-primary-100 dark:divide-primary-800">
              {[1, 2].map((i) => (
                <div key={i} className="grid grid-cols-4 gap-4 px-4 py-3">
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-16" />
                  <Skeleton className="h-4 w-20" />
                  <Skeleton className="h-4 w-16" />
                </div>
              ))}
            </div>
          </div>

          {/* Procurement/Program Details Section skeleton */}
          <DataSheetSkeleton sections={3} rows={3} showHeader />
        </div>

        {/* Sidebar */}
        <div className="space-y-4 lg:col-span-4">
          {/* Key Dates Timeline Widget skeleton */}
          <SidebarWidgetV2.Skeleton headerWidth="sm">
            <TimelineV2.Skeleton nodeCount={4} />
          </SidebarWidgetV2.Skeleton>

          {/* Awarding Agency Widget skeleton */}
          <SidebarWidgetV2.Skeleton headerWidth="md">
            <div className="space-y-3">
              <div className="space-y-1">
                <Skeleton className="h-2.5 w-20" />
                <Skeleton className="h-5 w-48" />
              </div>
              <div className="space-y-1">
                <Skeleton className="h-2.5 w-16" />
                <Skeleton className="h-5 w-40" />
              </div>
              <div className="space-y-1">
                <Skeleton className="h-2.5 w-12" />
                <Skeleton className="h-5 w-32" />
              </div>
            </div>
          </SidebarWidgetV2.Skeleton>

          {/* Recipient Details Widget skeleton */}
          <SidebarWidgetV2.Skeleton headerWidth="lg">
            <div className="space-y-3">
              <div className="space-y-1">
                <Skeleton className="h-2.5 w-16" />
                <Skeleton className="h-5 w-48" />
                <Skeleton className="h-3 w-32" />
              </div>
              <div className="space-y-1">
                <Skeleton className="h-2.5 w-20" />
                <Skeleton className="h-4 w-36" />
              </div>
              {/* Business types */}
              <div className="space-y-1">
                <Skeleton className="h-2.5 w-28" />
                <div className="flex flex-wrap gap-1">
                  <Skeleton className="h-5 w-24 rounded" />
                  <Skeleton className="h-5 w-28 rounded" />
                  <Skeleton className="h-5 w-20 rounded" />
                </div>
              </div>
            </div>
          </SidebarWidgetV2.Skeleton>

          {/* Place of Performance Widget skeleton */}
          <SidebarWidgetV2.Skeleton headerWidth="lg">
            <div className="space-y-1">
              <Skeleton className="h-4 w-36" />
              <Skeleton className="h-3 w-24" />
            </div>
          </SidebarWidgetV2.Skeleton>

          {/* Classification Codes Widget skeleton */}
          <SidebarWidgetV2.Skeleton headerWidth="lg">
            <div className="space-y-3">
              <div className="space-y-1">
                <Skeleton className="h-2.5 w-16" />
                <Skeleton className="h-5 w-20" />
                <Skeleton className="h-3 w-40" />
              </div>
              <div className="space-y-1">
                <Skeleton className="h-2.5 w-16" />
                <Skeleton className="h-5 w-16" />
                <Skeleton className="h-3 w-36" />
              </div>
            </div>
          </SidebarWidgetV2.Skeleton>
        </div>
      </div>
    </>
  )
}

AwardDisplayV2Skeleton.displayName = 'AwardDisplayV2Skeleton'

export { AwardDisplayV2Skeleton }
